import React, { useState } from 'react';
import { resolveDispute } from '../services/api';
import { reasonLabel } from '../constants/disputes';

export interface ResolveResult {
  dispute_id: number;
  status: 'upheld' | 'dismissed';
  resolved_at: string;
  dispute_count_after: number;
  new_badge_level: string;
}

interface DisputeResolveModalProps {
  dispute: {
    dispute_id: number;
    homeowner_name: string;
    homeowner_email: string;
    business_name: string;
    business_email: string;
    request_title: string;
    reason_category: string;
    description: string;
  };
  isOpen: boolean;
  onClose: () => void;
  onResolved: (result: ResolveResult) => void;
}

const MAX_NOTES = 2000;

// State machine: 'form' (initial; also where retryable errors surface inline),
// 'submitting', 'success', 'terminal' (409/404 — Close only, no retry).
type Phase = 'form' | 'submitting' | 'success' | 'terminal';

const DisputeResolveModal: React.FC<DisputeResolveModalProps> = ({ dispute, isOpen, onClose, onResolved }) => {
  const [phase, setPhase] = useState<Phase>('form');
  const [adminNotes, setAdminNotes] = useState('');
  const [pendingAction, setPendingAction] = useState<'upheld' | 'dismissed' | null>(null);
  const [formError, setFormError] = useState('');          // retryable error, shown inline above buttons
  const [terminalMessage, setTerminalMessage] = useState(''); // 409/404, Close-only
  const [result, setResult] = useState<ResolveResult | null>(null);

  if (!isOpen) return null;

  const handleResolve = async (resolution: 'upheld' | 'dismissed') => {
    setPhase('submitting');
    setPendingAction(resolution);
    setFormError('');
    try {
      const res = await resolveDispute(dispute.dispute_id, {
        resolution,
        admin_notes: adminNotes.trim() ? adminNotes.trim() : undefined,
      });
      setResult(res.data);
      setPhase('success');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        const existingStatus = err?.response?.data?.status ?? 'resolved';
        setTerminalMessage(`This dispute was already resolved (status: ${existingStatus}). Closing this modal.`);
        setPhase('terminal');
      } else if (status === 404) {
        setTerminalMessage('Dispute no longer exists. Closing.');
        setPhase('terminal');
      } else {
        setFormError('Something went wrong. Please try again.');
        setPhase('form'); // re-enable buttons for retry
      }
    } finally {
      setPendingAction(null);
    }
  };

  const handleSuccessClose = () => {
    if (result) onResolved(result);
    onClose();
  };

  const submitting = phase === 'submitting';

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h3 style={styles.title}>Resolve dispute</h3>

        {/* Read-only dispute summary — shown in every phase except after we've closed */}
        {phase !== 'success' && phase !== 'terminal' && (
          <div style={styles.summary}>
            <SummaryRow label="Homeowner" value={`${dispute.homeowner_name} — ${dispute.homeowner_email}`} />
            <SummaryRow label="Contractor" value={`${dispute.business_name} — ${dispute.business_email}`} />
            <SummaryRow label="Request" value={dispute.request_title} />
            <SummaryRow label="Reason" value={reasonLabel(dispute.reason_category)} />
            <div style={styles.summaryDescBlock}>
              <div style={styles.summaryLabel}>Description</div>
              <p style={styles.summaryDesc}>{dispute.description}</p>
            </div>
          </div>
        )}

        {/* FORM + SUBMITTING */}
        {(phase === 'form' || phase === 'submitting') && (
          <>
            <label style={styles.notesLabel}>Admin notes</label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value.slice(0, MAX_NOTES))}
              disabled={submitting}
              rows={3}
              maxLength={MAX_NOTES}
              placeholder="Optional notes for the homeowner (not visible to contractor)"
              style={styles.textarea}
            />
            <div style={styles.counter}>{adminNotes.length} / {MAX_NOTES}</div>

            <div style={styles.notesHint}>
              These notes appear in the homeowner's resolution email. Adding context is especially helpful when dismissing.
            </div>

            {formError && <div style={styles.errorText}>{formError}</div>}

            <div style={styles.actions}>
              <button onClick={onClose} disabled={submitting} style={styles.cancelBtn}>Cancel</button>

              <button onClick={() => handleResolve('dismissed')} disabled={submitting} style={styles.dismissBtn}>
                <span style={styles.btnMain}>
                  {submitting && pendingAction === 'dismissed' ? 'Dismissing…' : 'Dismiss — no penalty'}
                </span>
                <span style={styles.btnSub}>Mark resolved without consequence to contractor.</span>
              </button>

              <button onClick={() => handleResolve('upheld')} disabled={submitting} style={styles.upholdBtn}>
                <span style={styles.btnMain}>
                  {submitting && pendingAction === 'upheld' ? 'Upholding…' : 'Uphold — counts against contractor'}
                </span>
                <span style={styles.btnSub}>Will increment dispute count and may affect badge tier.</span>
              </button>
            </div>
          </>
        )}

        {/* SUCCESS */}
        {phase === 'success' && result && (
          <div style={styles.successPanel}>
            <div style={styles.successCheck}>✓</div>
            <p style={styles.successHeading}>Resolved successfully.</p>
            <div style={styles.resultGrid}>
              <ResultRow label="Status" value={result.status} />
              <ResultRow label="Dispute count after" value={String(result.dispute_count_after)} />
              <ResultRow label="Badge level" value={result.new_badge_level} />
            </div>
            <button onClick={handleSuccessClose} style={styles.closeBtn}>Close</button>
          </div>
        )}

        {/* TERMINAL ERROR (409 / 404) */}
        {phase === 'terminal' && (
          <div style={styles.terminalPanel}>
            <div style={styles.errorText}>{terminalMessage}</div>
            <button onClick={onClose} style={styles.closeBtn}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

const SummaryRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={styles.summaryRow}>
    <span style={styles.summaryLabel}>{label}</span>
    <span style={styles.summaryValue}>{value}</span>
  </div>
);

const ResultRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={styles.resultRow}>
    <span style={styles.resultLabel}>{label}</span>
    <span style={styles.resultValue}>{value}</span>
  </div>
);

const styles: { [key: string]: React.CSSProperties } = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  modal: { background: '#fff', borderRadius: 16, padding: 32, maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  title: { fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 16px' },
  summary: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 20 },
  summaryRow: { display: 'flex', gap: 10, marginBottom: 8, fontSize: 14, lineHeight: 1.4 },
  summaryLabel: { flexShrink: 0, width: 96, color: '#94a3b8', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.03em' },
  summaryValue: { color: '#0f172a', wordBreak: 'break-word' },
  summaryDescBlock: { marginTop: 4 },
  summaryDesc: { margin: '6px 0 0', fontSize: 14, color: '#334155', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  notesLabel: { display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 },
  textarea: { width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' },
  counter: { textAlign: 'right', fontSize: 12, color: '#94a3b8', margin: '4px 0 16px' },
  notesHint: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 13, color: '#1e40af', lineHeight: 1.4 },
  errorText: { color: '#dc2626', fontSize: 13, marginBottom: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 12px' },
  actions: { display: 'flex', flexDirection: 'column', gap: 10 },
  cancelBtn: { alignSelf: 'flex-start', padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  dismissBtn: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '12px 16px', borderRadius: 10, border: '1px solid #cbd5e1', background: '#f1f5f9', color: '#334155', cursor: 'pointer', textAlign: 'left', width: '100%' },
  upholdBtn: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '12px 16px', borderRadius: 10, border: '1px solid #f59e0b', background: 'linear-gradient(135deg, #ef4444, #f59e0b)', color: '#fff', cursor: 'pointer', textAlign: 'left', width: '100%' },
  btnMain: { fontSize: 15, fontWeight: 700 },
  btnSub: { fontSize: 12, fontWeight: 500, opacity: 0.85 },
  successPanel: { textAlign: 'center', padding: '8px 0' },
  successCheck: { fontSize: 40, color: '#10b981', lineHeight: 1 },
  successHeading: { margin: '8px 0 16px', fontSize: 18, fontWeight: 800, color: '#15803d' },
  resultGrid: { textAlign: 'left', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 20 },
  resultRow: { display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8, fontSize: 14 },
  resultLabel: { color: '#64748b', fontWeight: 600 },
  resultValue: { color: '#0f172a', fontWeight: 700 },
  terminalPanel: { paddingTop: 4 },
  closeBtn: { width: '100%', padding: 12, borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #3b82f6, #10b981)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
};

export default DisputeResolveModal;
